// LLM generated - Model Claude Opus 4
/**
 * useContestPostsState - Posts (unidirectional chat) state management
 *
 * Handles:
 * - Posts infinite query
 * - Unread posts tracking
 * - Last read post tracking
 * - Optimistic post updates
 * - Send post action
 * - Pin/unpin actions
 * - Reaction actions for posts
 *
 * Uses shared utilities from useContestChatUtils for deduplication and caching.
 */
import { useCallback } from 'react';
import { showNotification } from '@mantine/notifications';
import useTranslation from 'next-translate/useTranslation';

import { useOptimistic } from '~/common/hooks/useOptimistic';
import { logError } from '~/common/utils/log';
import {
  sendMessage as sendMessageAPI,
  addReaction as addReactionAPI,
  removeReaction as removeReactionAPI,
} from '~/services/live-activity/live-activity-service.service.api';
import type {
  ChatMessage,
  ChatMessagesInfiniteData,
  SendMessagePayload,
  SendMessageQuery,
  AddReactionPayload,
  Chat,
} from '~/services/live-activity/types';
import { useChatMessagesInfinite } from '~/services/live-activity/hooks/useChatMessagesInfinite';
import { useUnreadMessages } from '~/services/live-activity/hooks/useUnreadMessages';
import { useLastReadMessage } from '~/services/live-activity/hooks/useLastReadMessage';
import { usePinMessage } from '~/services/live-activity/hooks/usePinMessage';

import { MESSAGE_PAGE_SIZE } from '../constants/queryKeys';

import { applyOptimisticMessage, type ContestChatUtils } from './useContestChatUtils';

type UseContestPostsStateProps = {
  postsChat: Chat | undefined;
  user: {
    id: string;
    handle?: string;
    avatarUrl?: string;
  } | null;
  isChatOpen: boolean;
  isChatEnabled: boolean;
  contestName?: string;
  utils: ContestChatUtils;
};

export function useContestPostsState({
  postsChat,
  user,
  isChatOpen,
  isChatEnabled,
  contestName,
  utils,
}: UseContestPostsStateProps) {
  const { t } = useTranslation('chat');

  const {
    queryClient,
    postsQueryKey,
    generateOptimisticId,
    trackPendingMessage,
    clearPendingMessage,
    expectEventRef,
    trackPendingReaction,
    checkAndClearPendingReaction,
  } = utils;

  // --- Queries ---

  const {
    messages: serverPostsChatHistory,
    fetchNextPage: fetchNextPagePosts,
    hasNextPage: hasNextPagePosts,
    isFetchingNextPage: isFetchingNextPagePosts,
    isLoading: isLoadingPostsChat,
    error: postsChatError,
    totalCount: postsChatTotalCount,
    refetch: refetchPostsChat,
  } = useChatMessagesInfinite({
    chatId: postsChat?.id,
    enabled: !!postsChat?.id && postsChat?.isEnabled && isChatOpen,
    pageSize: MESSAGE_PAGE_SIZE,
  });

  const { data: unreadPostsData, refetch: refetchUnreadPosts } = useUnreadMessages({
    chatId: postsChat?.id,
    enabled: isChatEnabled && !!user && !!postsChat?.id,
  });

  const { markAsRead: markAsReadPosts, getLastSentMessageId: getLastSentPostsMessageId } =
    useLastReadMessage({ chatId: postsChat?.id });

  // Pin/unpin hook
  const {
    pinMessage,
    unpinMessage,
    isLoading: isPinUnpinLoading,
  } = usePinMessage({
    chatId: postsChat?.id,
  });

  // --- Optimistic State ---

  const [optimisticPostsChatHistory, addOptimisticPost, clearOptimisticPosts, isPostsPending] =
    useOptimistic(serverPostsChatHistory, applyOptimisticMessage);

  // --- Actions ---

  const sendPostMessage = useCallback(
    async (params: {
      message: string;
      header?: string;
      isPost?: boolean;
      notifyEntrants?: boolean;
    }): Promise<boolean> => {
      if (!postsChat?.id || !user?.handle) {
        logError('Cannot send post: missing chatId or user handle');
        return false;
      }

      const optimisticId = generateOptimisticId();

      // Use provided header or fall back to contestName
      const postHeader = params.header || contestName;

      const optimisticMsg: ChatMessage = {
        id: optimisticId,
        publishedAt: new Date().toISOString(),
        payload: { message: params.message, header: postHeader },
        user: { id: user.id, handle: user.handle, avatarUrl: user.avatarUrl },
        reactions: [],
        userReactions: [],
        updatedAt: new Date().toISOString(),
        isPinned: false,
        splashMessage: false,
      };

      trackPendingMessage(optimisticId, params.message, 'unidirectional');
      addOptimisticPost({ type: 'add', message: optimisticMsg });
      expectEventRef.current?.('message-created', 'unidirectional');

      try {
        const payload: SendMessagePayload = {
          message: params.message,
          handle: user.handle || '',
          header: postHeader,
          avatarUrl: user.avatarUrl,
        };
        const query: SendMessageQuery = { notifyReaders: params.notifyEntrants ?? true };
        await sendMessageAPI(postsChat.id, payload, query);

        return true;
      } catch (error) {
        clearPendingMessage(optimisticId);
        await queryClient.invalidateQueries({ queryKey: postsQueryKey });
        showNotification({
          title: t('notifications.error'),
          message: t('notifications.messageSendFailed'),
          color: 'red',
        });
        return false;
      }
    },
    [
      postsChat?.id,
      user,
      contestName,
      generateOptimisticId,
      trackPendingMessage,
      addOptimisticPost,
      clearPendingMessage,
      queryClient,
      postsQueryKey,
      expectEventRef,
      t,
    ]
  );

  const handleAddReaction = useCallback(
    async (messageId: string, reactionCode: string) => {
      if (!postsChat?.id || !user?.handle) {
        logError('Cannot add reaction: missing chatId or user handle');
        return;
      }

      trackPendingReaction(messageId, reactionCode, 'add');

      // Optimistic update
      queryClient.setQueryData<ChatMessagesInfiniteData>(postsQueryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).map((msg) => {
            if (msg.id !== messageId) return msg;

            const reactions = [...msg.reactions];
            const existingIdx = reactions.findIndex((r) => r.code === reactionCode);

            if (existingIdx >= 0) {
              reactions[existingIdx] = {
                ...reactions[existingIdx],
                count: reactions[existingIdx].count + 1,
              };
            } else {
              reactions.push({ code: reactionCode, count: 1 });
            }

            const userReactions = [...(msg.userReactions || [])];
            if (!userReactions.includes(reactionCode)) {
              userReactions.push(reactionCode);
            }

            return { ...msg, reactions, userReactions };
          }),
        }));

        return { ...oldData, pages: newPages };
      });

      expectEventRef.current?.('reaction-added', 'unidirectional');

      try {
        const payload: AddReactionPayload = {
          code: reactionCode,
          handle: user.handle || '',
          ...(user.avatarUrl && { avatarUrl: user.avatarUrl }),
        };
        await addReactionAPI(postsChat.id, messageId, payload);
      } catch (error) {
        checkAndClearPendingReaction(messageId, reactionCode, 'add');
        await queryClient.invalidateQueries({ queryKey: postsQueryKey });

        logError('Failed to add reaction:', error);
        showNotification({
          title: t('notifications.error'),
          message: t('notifications.messageSendFailed'),
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    [
      postsChat?.id,
      user,
      queryClient,
      postsQueryKey,
      trackPendingReaction,
      checkAndClearPendingReaction,
      expectEventRef,
      t,
    ]
  );

  const handleRemoveReaction = useCallback(
    async (messageId: string, reactionCode: string) => {
      if (!postsChat?.id) {
        logError('Cannot remove reaction: missing chatId');
        return;
      }

      trackPendingReaction(messageId, reactionCode, 'remove');

      // Optimistic update
      queryClient.setQueryData<ChatMessagesInfiniteData>(postsQueryKey, (oldData) => {
        if (!oldData?.pages) return oldData;

        const newPages = oldData.pages.map((page) => ({
          ...page,
          data: (page.data || []).map((msg) => {
            if (msg.id !== messageId) return msg;

            let reactions = [...msg.reactions];
            const existingIdx = reactions.findIndex((r) => r.code === reactionCode);

            if (existingIdx >= 0) {
              if (reactions[existingIdx].count <= 1) {
                reactions = reactions.filter((r) => r.code !== reactionCode);
              } else {
                reactions[existingIdx] = {
                  ...reactions[existingIdx],
                  count: reactions[existingIdx].count - 1,
                };
              }
            }

            const userReactions = (msg.userReactions || []).filter((code) => code !== reactionCode);

            return { ...msg, reactions, userReactions };
          }),
        }));

        return { ...oldData, pages: newPages };
      });

      expectEventRef.current?.('reaction-removed', 'unidirectional');

      try {
        await removeReactionAPI(postsChat.id, messageId, reactionCode);
      } catch (error) {
        checkAndClearPendingReaction(messageId, reactionCode, 'remove');
        await queryClient.invalidateQueries({ queryKey: postsQueryKey });
        logError('Failed to remove reaction:', error);
        showNotification({
          title: t('notifications.error'),
          message: t('notifications.messageSendFailed'),
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    [
      postsChat?.id,
      queryClient,
      postsQueryKey,
      trackPendingReaction,
      checkAndClearPendingReaction,
      expectEventRef,
      t,
    ]
  );

  return {
    // Posts data
    postsChatHistory: optimisticPostsChatHistory,
    serverPostsChatHistory,
    isLoadingPostsChat,
    postsChatError,
    refetchPostsChat,
    postsChatTotalCount,

    // Infinite scroll
    fetchNextPagePosts,
    hasNextPagePosts,
    isFetchingNextPagePosts,

    // Unread
    unreadPostsData,
    refetchUnreadPosts,

    // Last read
    markAsReadPosts,
    getLastSentPostsMessageId,

    // Pin/unpin
    pinMessage,
    unpinMessage,
    isPinUnpinLoading,

    // Actions
    sendPostMessage,
    handleAddReaction,
    handleRemoveReaction,

    // Optimistic state
    clearOptimisticPosts,
    isPostsPending,
  };
}

export type ContestPostsState = ReturnType<typeof useContestPostsState>;
